import { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "./AppShell";
import StatusBadge from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight } from "iconoir-react";
import { toast } from "sonner";

function AutoRerunToggle({ auditId, initial }) {
  const [enabled, setEnabled] = useState(initial);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    const next = !enabled;
    setBusy(true);
    try {
      await api.put(`/audits/${auditId}/auto-rerun`, { enabled: next });
      setEnabled(next);
      toast.success(next ? "Auto re-audit ON (weekly)" : "Auto re-audit OFF");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 pt-6 border-t border-border flex items-start justify-between gap-4" data-testid="auto-rerun-toggle">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Auto re-audit</div>
        <div className="mt-1 text-sm text-foreground">Re-run automatically every week to track score over time.</div>
      </div>
      <button
        onClick={toggle}
        disabled={busy}
        data-testid="auto-rerun-button"
        aria-pressed={enabled}
        className={`shrink-0 relative w-12 h-6 rounded-full transition-colors disabled:opacity-60 ${enabled ? "bg-intl" : "bg-border"}`}
      >
        <span className={`absolute top-0.5 ${enabled ? "left-6" : "left-0.5"} w-5 h-5 bg-white rounded-full transition-all`} />
      </button>
    </div>
  );
}

const PROGRESS_STAGES = ["queued", "crawling", "scoring", "explaining", "recommending", "complete"];
const STAGE_LABELS = {
  queued: "Queued",
  crawling: "Crawling your site…",
  scoring: "Scoring across 9 categories…",
  explaining: "AI is writing explanations…",
  recommending: "Generating recommendations…",
  complete: "Complete",
};

export default function AuditReport() {
  const { id } = useParams();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get(`/audits/${id}`);
        if (!mounted) return;
        setAudit(r.data);
        setLoading(false);
        if (["complete", "failed"].includes(r.data.status)) {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch {
        setLoading(false);
      }
    };
    load();
    pollRef.current = setInterval(load, 2500);
    return () => {
      mounted = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [id]);

  if (loading) {
    return (
      <AppShell title="Audit report" subtitle="// audit report">
        <div className="space-y-6" data-testid="audit-report-skeleton">
          <div className="border border-border bg-card p-8 space-y-4">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-9 w-2/3" />
            <Skeleton className="h-2 w-full" />
          </div>
          <div className="grid grid-cols-12 gap-4">
            <Skeleton className="col-span-12 md:col-span-6 h-48" />
            <Skeleton className="col-span-12 md:col-span-6 h-48" />
          </div>
        </div>
      </AppShell>
    );
  }
  if (!audit) {
    return <AppShell title="Audit not found"><div className="font-mono text-sm">Audit not found.</div></AppShell>;
  }

  const inProgress = !["complete", "failed"].includes(audit.status);
  const currentIdx = PROGRESS_STAGES.indexOf(audit.status);
  // Map stage index to a friendly percentage (queued=8% … recommending=88% … complete=100%).
  const percent = audit.status === "complete"
    ? 100
    : Math.min(92, Math.round(((Math.max(currentIdx, 0) + 1) / PROGRESS_STAGES.length) * 100));

  return (
    <AppShell
      title={audit.website_url}
      subtitle="// audit report"
      actions={
        audit.status === "complete" && (
          <Link to={`/audits/${id}/recommendations`} data-testid="open-recs-button" className="px-4 py-2 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold hover:bg-intl transition-colors">
            See recommendations →
          </Link>
        )
      }
    >
      <div data-testid="audit-report-page">
        {inProgress && (
          <div className="border border-border bg-card p-8 mb-6" data-testid="audit-progress">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">// running</div>
            <h2 className="mt-2 font-marketing text-3xl">{STAGE_LABELS[audit.status] || "Working…"}</h2>

            <div className="mt-6 flex items-center gap-4">
              <div className="flex-1 h-2 bg-muted overflow-hidden" data-testid="audit-progress-bar">
                <div
                  className="h-full bg-intl transition-all duration-700 ease-out"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <div className="font-mono text-sm tabular-nums w-12 text-right" data-testid="audit-progress-percent">{percent}%</div>
            </div>

            <div className="mt-6 grid grid-cols-5 gap-2">
              {PROGRESS_STAGES.slice(0, 5).map((s, i) => (
                <div key={s} className="flex flex-col gap-2">
                  <div className={`h-1 ${i <= currentIdx ? "bg-intl" : "bg-border"}`} />
                  <div className={`font-mono text-[10px] uppercase tracking-widest ${i <= currentIdx ? "text-foreground" : "text-muted-foreground"}`}>{s}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 font-mono text-xs text-muted-foreground">This usually takes ~60 seconds. This page updates automatically.</p>
          </div>
        )}

        {audit.status === "failed" && (
          <div className="border border-red-300 bg-red-50 p-8 mb-6">
            <div className="font-mono text-xs uppercase tracking-widest text-red-700">// failed</div>
            <h2 className="mt-2 font-marketing text-2xl">We couldn't complete this audit.</h2>
            <p className="mt-3 text-red-800 text-sm">{audit.error || "Unknown error"}</p>
            <Link to="/audits/new" className="inline-block mt-4 px-4 py-2 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold">Try another URL</Link>
          </div>
        )}

        {audit.status === "complete" && (
          <>
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
              <div className="col-span-12 md:col-span-6 border border-border bg-card p-8" data-testid="audit-score-tile">
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">AI Visibility Score</div>
                <div className="mt-4 flex items-end gap-4">
                  <div className="font-mono text-7xl lg:text-8xl leading-none" data-testid="audit-final-score">{audit.final_score}</div>
                  <div className="font-mono text-xl text-muted-foreground mb-2">/100</div>
                </div>
                <div className="mt-4"><StatusBadge label={audit.status_label} large /></div>
                <p className="mt-6 text-muted-foreground leading-relaxed">{audit.summary}</p>
              </div>

              <div className="col-span-12 md:col-span-6 border border-border bg-card p-8" data-testid="audit-meta-tile">
                <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Crawl summary</div>
                <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <dt className="text-muted-foreground">Status code</dt>
                  <dd className="font-mono">{audit.raw_metrics?.status_code ?? "—"}</dd>
                  <dt className="text-muted-foreground">Load time</dt>
                  <dd className="font-mono">{audit.raw_metrics?.load_ms} ms</dd>
                  <dt className="text-muted-foreground">Page size</dt>
                  <dd className="font-mono">{audit.raw_metrics?.page_size_kb} KB</dd>
                  <dt className="text-muted-foreground">Word count</dt>
                  <dd className="font-mono">{audit.raw_metrics?.word_count}</dd>
                  <dt className="text-muted-foreground">Internal links</dt>
                  <dd className="font-mono">{audit.raw_metrics?.links_internal}</dd>
                  <dt className="text-muted-foreground">Schemas detected</dt>
                  <dd className="font-mono">{(audit.raw_metrics?.schema_types || []).join(", ") || "none"}</dd>
                </dl>
                <AutoRerunToggle auditId={id} initial={audit.auto_rerun !== false} />
              </div>
            </div>

            <div className="mt-8">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">// breakdown</div>
              <h2 className="font-marketing text-3xl mt-1">Category scores</h2>
              <div className="mt-6 border border-border bg-card divide-y divide-border" data-testid="categories-list">
                {(audit.categories || []).map((c) => (
                  <div key={c.key} className="p-6 grid grid-cols-12 gap-4 items-start">
                    <div className="col-span-12 md:col-span-3">
                      <div className="font-app text-lg">{c.name}</div>
                      <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-1">weight {c.weight}</div>
                    </div>
                    <div className="col-span-12 md:col-span-2">
                      <div className="font-mono text-3xl">{c.score}<span className="text-muted-foreground text-base">/100</span></div>
                      <div className="mt-2 h-1.5 bg-muted">
                        <div className="h-full bg-intl" style={{ width: `${c.score}%` }} />
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-7">
                      {c.explanation && <p className="text-sm leading-relaxed">{c.explanation}</p>}
                      {c.findings?.length > 0 && (
                        <ul className="mt-2 space-y-1 font-mono text-xs text-muted-foreground">
                          {c.findings.map((f) => <li key={`${c.key}-${f}`}>· {f}</li>)}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-10 flex justify-end">
              <Link to={`/audits/${id}/recommendations`} className="inline-flex items-center gap-2 px-6 py-3 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold hover:bg-intl transition-colors" data-testid="bottom-open-recs-button">
                See recommendations <ArrowRight width={16} height={16} />
              </Link>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
